/*

  StatsView.ts

  User statistics display component for Gáta Dagsins

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ts } from "./i18n";
import { Component, m } from "./mithril";
import { UserRiddleStats } from "./types";
import { glyph } from "./util";

interface IStatsViewAttributes {
  stats: UserRiddleStats | null;
  loading?: boolean;
}

interface IStatItem {
  iconGlyph: string;
  label: string;
  value: number | string;
  highlight?: boolean;
}

export const StatsView: Component<IStatsViewAttributes> = {
  view: (vnode) => {
    const { stats, loading = false } = vnode.attrs;

    if (loading) {
      return m(".stats-view.loading", m(".loading-message", ts("Sæki tölfræði...")));
    }

    if (!stats) {
      return m(".stats-view.empty", m(".empty-message", ts("Engin tölfræði til að sýna")));
    }

    const statItems: IStatItem[] = [
      {
        iconGlyph: "fire",
        label: ts("Núverandi striklota"),
        value: stats.currentStreak,
        highlight: stats.currentStreak > 0
      },
      {
        iconGlyph: "star",
        label: ts("Lengsta striklota"),
        value: stats.longestStreak
      },
      {
        iconGlyph: "tower",
        label: ts("Hæsta skori náð"),
        value: stats.totalTopScores
      },
      {
        iconGlyph: "certificate",
        label: ts("Striklota hæsta skors"),
        value: stats.topScoreStreak,
        highlight: stats.topScoreStreak > 0
      },
      {
        iconGlyph: "calendar",
        label: ts("Heildarfjöldi daga"),
        value: stats.totalDaysPlayed
      },
    ];

    return m(".stats-view",
      m(".stats-grid",
        statItems.map((item, index) =>
          m(
            ".stat-item" + (item.highlight ? ".highlight" : ""),
            { key: index },
            [
              m(".stat-icon", glyph(item.iconGlyph)),
              m(".stat-info", [
                m(".stat-label", item.label),
                m(".stat-value", item.value.toString())
              ])
            ]
          )
        )
      )
    );
  }
};
